/**
 * Query Keys Factory
 *
 * Provides type-safe, hierarchical query keys for TanStack Query.
 * This ensures consistency and makes it easy to invalidate related queries.
 *
 * Usage:
 *   - Use queryKeys.auth.me() for auth/me query
 *   - Use queryKeys.courses.list() for courses list
 *   - Use queryKeys.courses.detail(id) for a specific course
 */

export const queryKeys = {
  // Auth queries
  auth: {
    all: ["auth"] as const,
    me: () => [...queryKeys.auth.all, "me"] as const,
    user: (id: string) => [...queryKeys.auth.all, "user", id] as const,
    activationInfo: (token: string | null | undefined) =>
      [...queryKeys.auth.all, "activation-info", token ?? "no-token"] as const,
  },

  // Dashboard queries
  dashboard: {
    all: ["dashboard"] as const,
    student: () => [...queryKeys.dashboard.all, "student"] as const,
    teacher: () => [...queryKeys.dashboard.all, "teacher"] as const,
    admin: () => [...queryKeys.dashboard.all, "admin"] as const,
    tenantSummary: () => [...queryKeys.dashboard.all, "tenantSummary"] as const,
    /**
     * Institute dashboard (api/v1/institutions/dashboard/*). `params` is the exact request
     * of the call, so each filter combination has its own cache entry; invalidate
     * `institute.all()` to refetch every section.
     */
    /** Tenant dashboard (api/v1/tenants/dashboard/*); `params` is the exact request. */
    tenant: {
      all: () => [...queryKeys.dashboard.all, "tenant"] as const,
      overview: (params: Record<string, unknown>) =>
        [...queryKeys.dashboard.tenant.all(), "overview", params] as const,
      institutions: (params: Record<string, unknown>) =>
        [...queryKeys.dashboard.tenant.all(), "institutions", params] as const,
      trends: (params: Record<string, unknown>) =>
        [...queryKeys.dashboard.tenant.all(), "trends", params] as const,
    },
    institute: {
      all: () => [...queryKeys.dashboard.all, "institute"] as const,
      overview: (params: Record<string, unknown>) =>
        [...queryKeys.dashboard.institute.all(), "overview", params] as const,
      attendance: (params: Record<string, unknown>) =>
        [...queryKeys.dashboard.institute.all(), "attendance", params] as const,
      academics: (params: Record<string, unknown>) =>
        [...queryKeys.dashboard.institute.all(), "academics", params] as const,
      engagement: (params: Record<string, unknown>) =>
        [...queryKeys.dashboard.institute.all(), "engagement", params] as const,
      doubts: (params: Record<string, unknown>) =>
        [...queryKeys.dashboard.institute.all(), "doubts", params] as const,
      teachers: (params: Record<string, unknown>) =>
        [...queryKeys.dashboard.institute.all(), "teachers", params] as const,
    },
    contentStudioSummary: () =>
      [...queryKeys.dashboard.all, "contentStudioSummary"] as const,
  },

  // Content Studio courses list (prefix must stay ["courses", "list"]; many
  // mutations invalidate it by that literal prefix)
  courses: {
    all: ["courses"] as const,
    lists: () => [...queryKeys.courses.all, "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.courses.lists(), filters] as const,
  },

  // Students list
  students: {
    all: ["students"] as const,
    lists: () => [...queryKeys.students.all, "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.students.lists(), filters] as const,
    details: () => [...queryKeys.students.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.students.details(), id] as const,
    /** Institute dropdown options of the add-student form. */
    institutionOptions: () =>
      [...queryKeys.students.all, "institution-options"] as const,
    /** Class dropdown options of the add-student form (depends on the selected board). */
    classOptions: (boardId?: string) =>
      [...queryKeys.students.all, "class-options", boardId ?? "no-board"] as const,
    /** Section dropdown options of the add-student form (depend on the selected institute and class). */
    sectionOptions: (instituteId?: string, classId?: string) =>
      [
        ...queryKeys.students.all,
        "section-options",
        instituteId ?? "no-institute",
        classId ?? "no-class",
      ] as const,
    /** Stream dropdown options of the add-student form (depend on the selected institute, board and class). */
    streamOptions: (instituteId?: string, boardId?: string, classId?: string) =>
      [
        ...queryKeys.students.all,
        "stream-options",
        instituteId ?? "no-institute",
        boardId ?? "no-board",
        classId ?? "no-class",
      ] as const,
  },

  /** Raw tenant-user GET by id (API item → mapped by feature hooks). Distinct from students.detail row cache. */
  tenantUsers: {
    all: ["tenantUsers"] as const,
    details: () => [...queryKeys.tenantUsers.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.tenantUsers.details(), id] as const,
  },

  /** Platform admin: raw user detail by id (includes tenant memberships). */
  platformUsers: {
    all: ["platformUsers"] as const,
    details: () => [...queryKeys.platformUsers.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.platformUsers.details(), id] as const,
  },

  teachers: {
    all: ["teachers"] as const,
    lists: () => [...queryKeys.teachers.all, "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.teachers.lists(), filters] as const,
    details: () => [...queryKeys.teachers.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.teachers.details(), id] as const,
    /** Institution dropdown options of the add-teacher form. */
    institutionOptions: () =>
      [...queryKeys.teachers.all, "institution-options"] as const,
    /** Teacher ↔ class mappings (GET api/v1/teacher/teaching-assignments); `params` is the request. */
    teachingAssignments: (params: Record<string, unknown>) =>
      [...queryKeys.teachers.teachingAssignmentsAll(), params] as const,
    /** Prefix of every teaching-assignments list (invalidated after a create / update / delete). */
    teachingAssignmentsAll: () => [...queryKeys.teachers.all, "teaching-assignments"] as const,
    /** Board options of one institute for the assignment dialog (GET api/v1/institutions/:id). */
    instituteBoards: (instituteId: string) =>
      [...queryKeys.teachers.all, "institute-boards", instituteId] as const,
  },

  institutions: {
    all: ["institutions"] as const,
    lists: () => [...queryKeys.institutions.all, "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.institutions.lists(), filters] as const,
    details: () => [...queryKeys.institutions.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.institutions.details(), id] as const,
    /** School admin users of one institution (GET api/v1/users, role school_admin). */
    adminLists: () => [...queryKeys.institutions.all, "admins"] as const,
    adminList: (filters?: Record<string, unknown>) =>
      [...queryKeys.institutions.adminLists(), filters] as const,
    adminDetail: (id: string) =>
      [...queryKeys.institutions.adminLists(), "detail", id] as const,
    /** Tenant dropdown options for the institution form. */
    tenantOptions: () =>
      [...queryKeys.institutions.all, "tenant-options"] as const,
  },

  /** Academic Setup (school admin). */
  academicSetup: {
    all: ["academicSetup"] as const,
    /** Every stream of one institute (GET api/v1/institutions/streams?institute_id=). */
    streams: (instituteId: string) =>
      [...queryKeys.academicSetup.all, "streams", instituteId] as const,
    /** Every page of one institute's sections (prefix for invalidation). */
    classSectionsAll: (instituteId: string) =>
      [...queryKeys.academicSetup.all, "class-sections", instituteId] as const,
    /**
     * One page of one institute's sections (GET api/v1/institutions/class-sections), for the
     * chosen board / class filters ("" = all).
     */
    classSections: (instituteId: string, page: number, boardId = "", classId = "") =>
      [...queryKeys.academicSetup.classSectionsAll(instituteId), { boardId, classId }, page] as const,
    /** Every page of one institute's academic terms (prefix for invalidation). */
    academicTermsAll: (instituteId: string) =>
      [...queryKeys.academicSetup.all, "academic-terms", instituteId] as const,
    /** One page of one institute's academic terms (GET api/v1/institutions/academic-terms). */
    academicTerms: (instituteId: string, page: number) =>
      [...queryKeys.academicSetup.academicTermsAll(instituteId), page] as const,
  },

  /** Time Table (school admin). All data is scoped to one academic term. */
  timetable: {
    all: ["timetable"] as const,
    /** The institute's academic terms, for the term picker. */
    termOptions: (instituteId: string) =>
      [...queryKeys.timetable.all, "term-options", instituteId] as const,
    /** Every version of one term (prefix for invalidation). */
    versionsAll: (termId: string) =>
      [...queryKeys.timetable.all, "versions", termId] as const,
    /** One page of a term's versions (GET /timetable/versions?term_id=&page=). */
    versions: (termId: string, page: number) =>
      [...queryKeys.timetable.versionsAll(termId), page] as const,
    /** One version's detail (GET /timetable/versions/:id). */
    version: (versionId: string) =>
      [...queryKeys.timetable.all, "version", versionId] as const,
    /** A class or teacher weekly grid of a version (GET /timetable/versions/:id/grid). */
    grid: (versionId: string, ref: { classSectionId?: string; teacherId?: string }) =>
      [...queryKeys.timetable.version(versionId), "grid", ref] as const,
    /** Bell schedule of a term's active version (GET /timetable/bell-schedule?term_id=). */
    bellSchedule: (termId: string) =>
      [...queryKeys.timetable.all, "bell-schedule", termId] as const,
    /** Holidays of a term (GET /timetable/holidays?term_id=). */
    holidays: (termId: string) =>
      [...queryKeys.timetable.all, "holidays", termId] as const,
    /** Every page of a term's uploads (prefix for invalidation). */
    uploadsAll: (termId: string) =>
      [...queryKeys.timetable.all, "uploads", termId] as const,
    /** One page of a term's uploads (GET /timetable/uploads?term_id=&page=). */
    uploads: (termId: string, page: number, status = "") =>
      [...queryKeys.timetable.uploadsAll(termId), { status }, page] as const,
    /** One upload's detail + report (GET /timetable/uploads/:id). */
    upload: (uploadId: string) =>
      [...queryKeys.timetable.all, "upload", uploadId] as const,
    /** Preview of an uploaded class (GET /timetable/uploads/:id/preview?class_code=). */
    uploadPreview: (uploadId: string, classCode = "") =>
      [...queryKeys.timetable.all, "upload-preview", uploadId, classCode] as const,
    /** Bell schedule of a term's active version (GET /timetable/bell-schedule?term_id=). */
    bellScheduleByTerm: (termId: string) =>
      [...queryKeys.timetable.all, "bell-schedule-term", termId] as const,
    /** One holiday's detail (GET /timetable/holidays/:id). */
    holiday: (holidayId: string) =>
      [...queryKeys.timetable.all, "holiday", holidayId] as const,
  },

  announcements: {
    all: ["announcements"] as const,
    lists: () => [...queryKeys.announcements.all, "list"] as const,
    /** `filters` is the exact request params of the list call. */
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.announcements.lists(), filters] as const,
    /** Counts of GET api/v1/announcements/admin/summary (tab badges). */
    summary: () => [...queryKeys.announcements.all, "summary"] as const,
    /** Institution dropdown options (list filter + create form). */
    institutionOptions: (tenantId?: string) =>
      [
        ...queryKeys.announcements.all,
        "institution-options",
        tenantId ?? null,
      ] as const,
  },

  contentCreators: {
    all: ["contentCreators"] as const,
    lists: () => [...queryKeys.contentCreators.all, "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.contentCreators.lists(), filters] as const,
    details: () => [...queryKeys.contentCreators.all, "detail"] as const,
    detail: (id: string) =>
      [...queryKeys.contentCreators.details(), id] as const,
  },

  // Tenants list (admin settings)
  tenants: {
    all: ["tenants"] as const,
    lists: () => [...queryKeys.tenants.all, "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.tenants.lists(), filters] as const,
    details: () => [...queryKeys.tenants.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.tenants.details(), id] as const,
  },

  // Personalization / auth lookup values
  personalization: {
    all: ["personalization"] as const,
    states: () => [...queryKeys.personalization.all, "states"] as const,
    boards: () => [...queryKeys.personalization.all, "boards"] as const,
    classes: () => [...queryKeys.personalization.all, "classes"] as const,
  },

  // Curriculum (boards/classes/subjects)
  boards: {
    all: ["boards"] as const,
    lists: () => [...queryKeys.boards.all, "list"] as const,
    /** Without an institute id the key is unchanged; with one, each institute has its own entry. */
    list: (instituteId?: string) =>
      instituteId
        ? ([...queryKeys.boards.lists(), { instituteId }] as const)
        : ([...queryKeys.boards.lists()] as const),
    classes: (boardId: number | string | undefined) =>
      [...queryKeys.boards.all, "classes", boardId ?? "no-board"] as const,
    /** Classes of a board from the helpers lookup (`{ classes: [...] }`). */
    classLookup: (boardId: number | string | undefined) =>
      [...queryKeys.boards.all, "class-lookup", boardId ?? "no-board"] as const,
    /** Subjects of a board + class from the helpers lookup (`{ subjects: [...] }`). */
    subjectLookup: (
      boardId: number | string | undefined,
      classId: number | string | undefined,
    ) =>
      [
        ...queryKeys.boards.all,
        "subject-lookup",
        boardId ?? "no-board",
        classId ?? "no-class",
      ] as const,
    subjects: (
      boardId: number | string | undefined,
      classId: number | string | undefined,
    ) =>
      [
        ...queryKeys.boards.all,
        "subjects",
        boardId ?? "no-board",
        classId ?? "no-class",
      ] as const,
  },

  // Content Studio course categories (generic / competitive)
  courseCategories: {
    all: ["courseCategories"] as const,
    list: () => [...queryKeys.courseCategories.all, "list"] as const,
  },
} as const;
